/**
 * Transient notices, shown at the top of the viewport rather than the top of the page.
 *
 * The pages this matters on are long: the skill editor runs to several screens, and the save button is
 * at the bottom of it. A result rendered into the document meant scrolling back up to find out whether
 * the thing you just did worked, which is the one moment you should not have to go looking.
 *
 * Deliberately not used for everything. A message that belongs beside a field — a password that was
 * refused, a code that is taken — stays where the field is, because that is where the eye already is and
 * those pages are one screen anyway. This is for the result of an action: saved, deleted, refreshed,
 * failed.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type ToastKind = 'ok' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  /** Something worked. Clears itself. */
  ok: (message: string) => void;
  /** Something did not. Stays until dismissed, because a failure outliving a glance is the point. */
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long a success stays. Long enough to read twice, short enough not to sit over the next thing. */
const OK_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  // Cleared on unmount so a timer cannot fire into a page that has gone.
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = next.current++;
      setToasts((list) => [...list, { id, kind, message }]);
      // An error stays put. Everything else goes on its own, because the page behind it is the thing
      // being worked on and a stack of congratulations is in the way.
      if (kind !== 'error') {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), OK_MS),
        );
      }
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) clearTimeout(timer);
      timers.current.clear();
    },
    [],
  );

  const api = useMemo<ToastApi>(
    () => ({
      ok: (message) => push('ok', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        aria-live so a screen reader is told without the focus moving, which would take the keyboard away
        from whatever was just being used. Assertive for failures only: a success that interrupts is worse
        than a success that waits.
      */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role={t.kind === 'error' ? 'alert' : undefined}>
            <span>{t.message}</span>
            <button type="button" className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast needs a ToastProvider above it');
  return api;
}
